import * as React from 'react';
import ScrollToTop from "react-scroll-to-top";
import ChapterNav from './components/ChapterNav';
import MetricCards from './components/MetricCards';
import PullQuote from './components/PullQuote';
import CompactImageCard from './components/CompactImageCard';
import ImageSubsection from './components/ImageSubsection';
import Footer from "../../toolbar/Footer";
import image_ABApp from "../../images/design/ABAppOneLineStrategy.png";
import image_DesignOrg from "../../images/design/DesignOrg.png";
import image_HealthCheck from '../../images/design/HealthCheck.jpg';
import image_MacroFactors from '../../images/design/MacroFactors.png';
import image_MicroFactors from '../../images/design/MicroFactors.png';
import image_PopupInsideFloatingSection from '../../images/design/PopupInsideFloatingSection.png';
import image_TemplateTable from '../../images/design/TemplateTable.png';
import image_UserJourneyMapping from '../../images/design/UserJourneyMapping.jpg';
import image_UserPersona from '../../images/design/UserPersona.jpg';
import image_Banner from '../../images/design/mgmt_banner.png';
import './DesignManagement.css';
import PageTransition from '../../pageTransition';

const CHAPTERS = [
    { id: 'ch-organization', label: 'Organization' },
    { id: 'ch-process', label: 'Standardization' },
    { id: 'ch-strategy', label: 'Alignment' },
    { id: 'ch-philosophy', label: 'Principles' },
    { id: 'ch-impact', label: 'Impact' },
];

const METRICS = [
    { value: 82, suffix: '%', label: 'Customer Adoption' },
    { value: 5, suffix: 'x', label: 'Faster Demo Prep' },
    { value: 40, suffix: '%', label: 'Less Rework' },
];

const DesignManagement = (props) => {
    return (
        <>
        <PageTransition>
        <div className="mainBox">
            <div className="aiStudio project odd">
                <div className="project-inner">
                    <ImageSubsection
                        title="Design Management"
                        blue={true}
                        banner={true}
                        images={[
                            { image: image_Banner, width: '60rem', height: '35rem' },
                        ]}
                        description='Building design culture, operations, and team strategy from the ground up<br/><br/><span style="opacity: 0.6">ROLE:</span> Head of Design / Product Manager'
                    />

                    <div className="mgmt-spreads">
                        {/* ========================================= */}
                        {/* SPREAD 1: Organization                    */}
                        {/* ========================================= */}
                        <div className="spread-organization" id="ch-organization">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">01</span>
                                <span className="spread-chapter-label">Organization</span>
                                <p className="spread-chapter-desc">
                                    I implemented a hybrid design team structure showing how designers work within different departments while reporting back to the team. I also strategized future team growth, included design work packages in SoWs to ensure fair budget allocation, and benchmarked our maturity on the Forrester/NASDAQ design scale.
                                </p>
                            </div>

                            <div className="spread-cell--org-img" style={{ gridArea: 'oi' }}>
                                <CompactImageCard
                                    image={image_DesignOrg}
                                    title="Design Organisation Chart"
                                />
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 2: Process                         */}
                        {/* ========================================= */}
                        <div className="spread-process" id="ch-process">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">02</span>
                                <span className="spread-chapter-label">Standardization</span>
                                <h3 className="spread-chapter-heading">Design Rationale</h3>
                                <p className="spread-chapter-desc">
                                    Team members would often forget what design decisions were made and why, leading to the same discussions multiple times. I created guidelines and templates to document decisions in a standardized way by compiling macro and micro factors. This became a standard process across all design projects.
                                </p>
                            </div>

                            {/* --- 3-across cards: micro, macro, template --- */}
                            <div className="spread-cell--card1" style={{ gridArea: 'c1' }}>
                                <CompactImageCard
                                    image={image_MicroFactors}
                                    title="Micro Factors"
                                    caption="Compiled to defend design decisions in a standardized way"
                                />
                            </div>
                            <div className="spread-cell--card2" style={{ gridArea: 'c2' }}>
                                <CompactImageCard
                                    image={image_MacroFactors}
                                    title="Macro Factors"
                                    caption="Example sentences to set the tone for design rationale"
                                />
                            </div>
                            <div className="spread-cell--card3" style={{ gridArea: 'c3' }}>
                                <CompactImageCard
                                    image={image_TemplateTable}
                                    title="Decision Template"
                                    caption="Date, participants, decisions, and FAQs in one place"
                                />
                            </div>

                            {/* --- Workshop section --- */}
                            <div className="spread-cell--workshop" style={{ gridArea: 'ws' }}>
                                <div className="spread-cell--workshop-text">
                                    <h3>Design Workshop Standardization</h3>
                                    <p className="spread-chapter-desc">Created ready-to-use agenda documents and templates to standardize how workshops are run and documented.</p>
                                </div>
                                <div className="spread-cell--workshop-thumbs">
                                    <CompactImageCard image={image_UserPersona} title="User Persona" />
                                    <CompactImageCard image={image_UserJourneyMapping} title="Journey Mapping" />
                                    <CompactImageCard image={image_HealthCheck} title="Health Check" />
                                </div>
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 3: Strategy                       */}
                        {/* ========================================= */}
                        <div className="spread-thinking" id="ch-strategy">

                            {/* --- Strategy header + description --- */}
                            <div className="spread-cell--strategy-text" style={{ gridArea: 'st' }}>
                                <span className="spread-chapter-number">03</span>
                                <span className="spread-chapter-label">Alignment</span>
                                <h3>One Line Strategy Statement</h3>
                                <p>
                                    For an early product, I ran a workshop to summarize the problem, audience, and solution in a single sentence. The exercise surfaced vision misalignments within the management team and guided them toward a shared strategy. After this, I encouraged every team and customer to use this approach to confront disagreements early.
                                </p>
                            </div>

                            {/* --- Strategy image (ABApp, slight tilt) --- */}
                            <div className="spread-cell--strategy-img" style={{ gridArea: 'si' }}>
                                <CompactImageCard
                                    image={image_ABApp}
                                    title="Augmented Banking App Strategy"
                                    tilt={true}
                                />
                            </div>

                            {/* --- UX KPIs image --- */}
                            <div className="spread-cell--kpi-img" style={{ gridArea: 'ki' }}>
                                <CompactImageCard
                                    image={image_PopupInsideFloatingSection}
                                    title="UX KPIs — Floating Section Concept"
                                />
                            </div>

                            {/* --- UX KPIs text --- */}
                            <div className="spread-cell--kpi-text" style={{ gridArea: 'kt' }}>
                                <h3>UX KPIs</h3>
                                <p>
                                    I defined a UX measurement framework — selecting SUS, NPS, and CSAT as core KPIs — and drove alignment between design and business goals to ensure adoption metrics were tracked consistently. To overcome enterprise data-sensitivity constraints, I directed the team to build compliant, in-product feedback mechanisms: popup widgets and floating dashboard sections triggered by task completion or session duration. This closed the loop between design decisions and measurable outcomes.
                                </p>
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 4: Philosophy                     */}
                        {/* ========================================= */}
                        <div className="spread-philosophy" id="ch-philosophy">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">04</span>
                                <span className="spread-chapter-label">Principles</span>
                                <p className="spread-chapter-desc">
                                    Two principles that transformed how my design team operated and delivered.
                                </p>
                            </div>

                            {/* --- Pull quotes side by side --- */}
                            <div className="spread-cell--quote-left" style={{ gridArea: 'ql' }}>
                                <PullQuote
                                    text="Design is Not a Democracy"
                                    body="Design triggers emotions, and emotions trigger opinions. I separated serious feedback from casual by requiring written submissions via a dedicated email address — immediately improving quality. I also established that authority goes hand in hand with responsibility: if the design team doesn't agree with a decision, it won't own its outcome. Quality of life improved drastically."
                                    variant="warm"
                                    revealDirection="slideRight"
                                />
                            </div>
                            <div className="spread-cell--quote-right" style={{ gridArea: 'qr' }}>
                                <PullQuote
                                    text="Design Freeze"
                                    body="Design is never truly finished, but it needs to be paused for handover, implementation, and testing. Inspired by code freeze, I introduced 'design freeze' into the project lifecycle. Project managers quickly adopted it, and we could push back on never-ending changes without hesitation."
                                    variant="cool"
                                    revealDirection="slideLeft"
                                />
                            </div>
                        </div>

                        {/* ===== IMPACT METRICS ===== */}
                        <section id="ch-impact">
                            <MetricCards title="Impact" metrics={METRICS} />
                        </section>
                    </div>
                </div>
            </div>
            <Footer/>
        </div>
        </PageTransition>
        <ScrollToTop smooth className="scroll-to-top" />
        <ChapterNav chapters={CHAPTERS} />
        </>
    );
};

export default DesignManagement;
